import React from 'react';
import { useTranslation } from 'react-i18next';
import * as z from 'zod/v3';
import { zodResolver } from '@hookform/resolvers/zod';
import { isValid, parseISO } from 'date-fns';
import { useForm } from 'react-hook-form';
import { relationTypes } from 'Modules/financing/config/relationTypes';
export const getSocialSecurityNumberRegex = (requiresFullSsn = false) =>
  requiresFullSsn ? /^\d{3}-?\d{2}-?\d{4}$/ : /^(?:\d{3}-?\d{2}-?\d{4}|\d{4})$/;

export const validateISODateFormat = (val) => {
  return val ? isValid(parseISO(val)) : true;
};

const relationValues = relationTypes.map((item) => item.value);
const useCreateApplicationForm = (errorConfig = {}) => {
  const { t } = useTranslation();

  const schema = React.useMemo(() => {
    return z.object({
      // salutation: z.string().min(1,{ message: t('common.requiredSalutation') }),
      firstName: z.string().min(1, { message: t('common.requiredFieldFirstName') }),
      lastName: z.string().min(1, { message: t('common.requiredFieldLastName') }),
      address: z.string().min(1, { message: t('common.requiredFieldStreet') }),
      city: z.string().min(1, { message: t('common.requiredFieldCity') }),
      state: z.string().min(1, { message: t('common.requiredFieldState') }),
      zip: z.string().min(1, { message: t('common.requiredFieldPostalCode') }),
      relationship: z.enum(relationValues, {
        errorMap: () => {
          return { message: 'relationship is required' };
        },
      }),
      // country: z.string().min(1,{ message: t('common.requiredFieldCountry') }),
      email: z
        .string()
        .min(1, { message: t('common.requiredFieldEmail') })
        .email({ message: t('common.invalidFormatEmail') }),
      socialSecurityNumber: z
        .string()
        .min(1, { message: t('common.requiredFieldSSN') })
        .refine(
          (val) => {
            const regex = getSocialSecurityNumberRegex(errorConfig.requiresFullSsn);
            return regex.test(val);
          },
          {
            message: errorConfig.requiresFullSsn
              ? t(
                  'patientfi.formlabel.fullSsnRequired',
                  'Please enter your full 9-digit Social Security Number'
                )
              : t('patientfi.formlabel.invalidFormatSSN'),
          }
        ),
      phone: z
        .string()
        .min(1, { message: t('common.requiredFieldPhone') })
        .regex(/^\d{10}$/, { message: 'Please provide a valid phone number with 10 digits' }),
      dateOfBirth: z
        .any()
        .refine(validateISODateFormat, { message: t('common.invalidDateFormat') }),
      annualIncome: z
        .string()
        .transform(parseInt)
        .refine((val) => val >= 1, { message: t('common.requiredFieldAnnualIncome') }),
      terms: z.boolean().refine((val) => val, { message: t('common.requiredFieldTerms') }),
    });
  }, [t, errorConfig.requiresFullSsn]);

  const form = useForm({
    resolver: zodResolver(schema),
  });

  const { setError } = form;

  // Set initial errors based on errorConfig
  React.useEffect(() => {
    if (errorConfig.affectedFields && Array.isArray(errorConfig.affectedFields)) {
      errorConfig.affectedFields.forEach((fieldName) => {
        // Set a generic error message or use a custom one from errorConfig
        const errorMessage = errorConfig.message || t('common.fieldHasError');
        setError(fieldName, {
          type: 'manual',
          message: errorMessage,
        });
      });
    }
  }, [errorConfig, setError, t]);

  return form;
};

export default useCreateApplicationForm;
